<script lang="ts">
  // The image side of the long activity card: the street view, route map and power chart that were
  // generated for this activity.
  //
  // Only the `lg` card puts the frame beside the details as a whole; there it stretches to the
  // card's height, and if that height has room for more than one image without squashing them, it
  // shows more than one -- an activity usually has two or three thumbnails, and a tall card would
  // otherwise waste the space on grey. Every other tier shows a single image at its natural size:
  // `md` beside the title only, at a fixed height so that a ride with no elevation profile next to
  // it doesn't get a shorter picture than one that has; `xs`/`sm` above the details, centred on the
  // grey rather than stretched across the card, which at those widths would tower over the text.
  //
  // Clicking anywhere on the frame pages through whatever doesn't fit. Every slot but the last is
  // pinned to the image it starts on; only the bottom slot rotates, through the images left over.
  // So with three images and two slots there are two pages -- [street view, map] and
  // [street view, chart] -- and the top image never moves out from under the pointer.
  import { cn } from '@/lib/utils';
  import { handleImageError } from '@/Util/utils';
  import { Activity } from '@/Common/ActivityFunctions';
  import type { ActivityImage, CardLayout } from './activityCardUtils';

  export let images: ActivityImage[] = [];
  /** The activity's medal, for the breakthrough ribbon drawn over the top image. */
  export let medal: number | null | undefined = null;
  export let layout: CardLayout = 'lg';

  /**
   * How tall a slot has to be before splitting the frame is worth it, as a fraction of the frame's
   * width. The generated thumbnails are 354x200 (see GoogleUrlSigner / generatePolylineStaticMap),
   * so an image drawn at the frame's full width is about 0.57 of it tall -- ask for a shade more
   * than that and a slot we do create is nearly always tall enough to show its image full width.
   * The floor keeps it sane if the frame is ever very narrow.
   */
  const MIN_SLOT_RATIO = 0.58;
  const MIN_SLOT_HEIGHT = 120;
  const SLOT_GAP = 4; // px, matches the gap-1 below
  /** The frame's own border and padding, which the measured border-box height includes. */
  const FRAME_INSET = 10;

  const medalRibbons: Record<number, { label: string; classes: string }> = {
    [-1]: { label: 'Near Breakthrough', classes: 'bg-fake-br' },
    1: { label: 'Bronze Breakthrough', classes: 'bg-bronze-br' },
    2: { label: 'Silver Breakthrough', classes: 'bg-silver-br' },
    3: { label: 'Gold Breakthrough', classes: 'bg-gold-br' },
  };
  $: ribbon = medalRibbons[medal ?? 0] ?? null;

  /** Stacked above the details there is no height to fill, so it's one image at its own aspect. */
  $: stacked = layout === 'xs' || layout === 'sm';
  /** Beside the title only: a fixed frame, so the row's height doesn't depend on the title's. */
  $: single = layout === 'md';

  // An activity whose thumbnails were never generated still gets the generic placeholder rather
  // than a blank grey column, which is what the card showed before this was split out.
  $: shownImages = images.length
    ? images
    : [{ url: Activity.getBackupImageUrl(), type: 'placeholder' } as ActivityImage];

  // The slots live in an absolutely positioned column, so however many there are they can't feed
  // back into the frame's height -- which comes from the details column beside it.
  let frameSize: ResizeObserverSize[] | null = null;
  let frameWidth = 0;
  let frameHeight = 0;
  $: if (frameSize && frameSize[0]) {
    frameWidth = frameSize[0].inlineSize;
    frameHeight = frameSize[0].blockSize;
  }

  $: contentHeight = Math.max(0, frameHeight - FRAME_INSET);
  $: slotCount = stacked || single ? 1 : slotsThatFit(frameWidth, contentHeight, shownImages.length);
  function slotsThatFit(width: number, height: number, available: number): number {
    if (available <= 1 || width <= 0 || height <= 0) return 1;
    const minSlot = Math.max(MIN_SLOT_HEIGHT, width * MIN_SLOT_RATIO);
    const fits = Math.floor((height + SLOT_GAP) / (minSlot + SLOT_GAP));
    return Math.max(1, Math.min(fits, available));
  }

  /**
   * The most any one image may be drawn at. Capping each image rather than giving it a slot to be
   * centred in is what keeps them stacked against each other: with width and height both auto, the
   * element's box ends up exactly the size of the picture, so the column has no dead space in it to
   * spread them apart, and justify-center then floats the whole group in the middle of the frame.
   */
  $: slotMaxHeight = Math.max(0, (contentHeight - SLOT_GAP * (slotCount - 1)) / slotCount);

  // The images that don't fit are exactly the ones past the last slot, so the bottom slot has
  // `shownImages.length - slotCount + 1` images to rotate between and every slot above it is fixed.
  // Reset when the *set* of images changes, not when the array's identity does -- the parent hands
  // us a freshly built list on every re-render, and paging shouldn't jump back on a favourite.
  let page = 0;
  $: imageKey = shownImages.map((image) => image.url).join('|');
  $: imageKey, (page = 0);
  $: pageCount = Math.max(1, shownImages.length - slotCount + 1);
  // Clamp rather than reset: growing the frame adds a slot and can leave `page` past the end.
  $: page = Math.min(page, pageCount - 1);
  $: visible = [...shownImages.slice(0, slotCount - 1), shownImages[slotCount - 1 + page]].filter(Boolean);
  $: canAdvance = pageCount > 1;

  function advance(event: MouseEvent) {
    if (!canAdvance) return;
    // The whole card is a click target on the dashboards; paging the images isn't opening it.
    event.stopPropagation();
    page = (page + 1) % pageCount;
  }

  /** Which images are on screen right now, for the position dots. */
  $: shown = new Set([...Array(Math.max(0, slotCount - 1)).keys(), slotCount - 1 + page]);

  const altFor = (image: ActivityImage): string =>
    image.type === 'map' ? 'Activity route map' : image.type === 'chart' ? 'Activity power chart' : image.type === 'street_view' ? 'Activity street view' : 'Activity';
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  bind:borderBoxSize={frameSize}
  class={cn(
    'relative overflow-hidden rounded-md border border-border',
    'bg-muted shrink-0',
    // The single frame fixes the height only and takes its width from the picture, so there is no
    // grey either side of it; the stacked frame caps the picture at the 300px the `lg` column gives
    // it, so reflowing the card doesn't resize it.
    stacked ? 'w-full' : single ? 'h-[150px] self-start' : 'w-[300px] self-stretch min-h-[190px]',
    canAdvance ? 'cursor-pointer transition hover:brightness-95' : ''
  )}
  on:click={advance}
>
  {#if stacked}
    {#if visible[0]}
      <!-- Centred rather than stretched: blown up to the full width of the card the thumbnail is
           both soft and taller than everything under it, so it keeps the size it has elsewhere and
           the frame's grey shows either side of it. -->
      <div class="flex flex-row items-center justify-center">
        <img
          class={cn("block w-full h-auto", layout != 'xs' ? 'max-w-[300px]' : '')}
          src={visible[0].url}
          alt={altFor(visible[0])}
          draggable="false"
          on:error={handleImageError}
        />
      </div>
    {/if}
  {:else if single}
    {#if visible[0]}
      <!-- Height fixed, width from the aspect ratio: the picture fills the frame exactly, so a ride
           whose thumbnail is narrower than the usual 354x200 gets a narrower frame rather than grey
           down both sides of it. -->
      <img
        class="block h-full w-auto"
        src={visible[0].url}
        alt={altFor(visible[0])}
        draggable="false"
        on:error={handleImageError}
      />
    {/if}
  {:else}
    <div class="absolute inset-0 flex flex-col items-center justify-center gap-1 py-1">
      {#each visible as image (image.url)}
        <img
          class="max-w-full object-contain"
          style="max-height: {slotMaxHeight}px"
          src={image.url}
          alt={altFor(image)}
          draggable="false"
          on:error={handleImageError}
        />
      {/each}
    </div>
  {/if}

  {#if ribbon}
    <span class={cn('absolute top-2 left-2 rounded-full px-2 py-0.5 text-[11px] font-medium text-white shadow-sm', ribbon.classes)}>
      <i class="fa-solid fa-award mr-1" aria-hidden="true"></i>{ribbon.label}
    </span>
  {/if}

  {#if canAdvance}
    <div class="absolute bottom-2 left-1/2 -translate-x-1/2 flex flex-row gap-1">
      {#each shownImages as _, index}
        <span class={cn('h-1.5 w-1.5 rounded-full ring-1 ring-black/10', shown.has(index) ? 'bg-foreground/70' : 'bg-foreground/25')} />
      {/each}
    </div>
  {/if}
</div>
